import '@/polyfills.js'

import '@/assets/styles/fonts.css'
import '@/assets/styles/global.scss'
import { newRelicReadyPromise } from '@/external_scripts'
import '@/plugins/elbwalker'
import '@/services/SiteConfig.js'
import {
  isChunkOrAssetFailure,
  tryReloadForChunkError
} from '@/services/helpers/ChunkLoadRecovery.js'
// The header, cart and many templates use the grid as plain classes
// (`class="v-row"`, `v-col-md-3`) without rendering VRow/VCol, so Vuetify's
// per-component style import never runs for them. Without this line the grid
// CSS only arrives with the first chunk that imports VRow/VCol, and a page that
// has none (category, collection, cart) renders without it (VIP-12356).
// The prebuilt file is what the settings compile to, since they leave the grid
// alone; the VRow chunk keeps its identical copy, which is harmless.
import 'vuetify/lib/components/VGrid/VGrid.css'

import { createVicampoApp } from '@/app/createApp'
import { getComponentDefaults } from '@/plugins/vuetify.js'
import { HIGH_CONTRAST_TOGGLE } from '@/stores/accessibility'
import { watch } from 'vue'

declare global {
  interface Window {
    /** Serialized Pinia state embedded by the SSR response (VIP-12037). */
    __PINIA_STATE__?: import('pinia').StateTree
    /**
     * Viewport the server framed its render with (VIP-12333). Seeds Vuetify's
     * display for the hydration pass so breakpoint-dependent markup matches;
     * Vuetify swaps in the real window size right after mount.
     */
    __SSR_VIEWPORT__?: import('@/app/resolveSsrViewport').SsrViewport
  }
}

const MOUNT_POINT = '#app'

function setupChunkLoadErrorRecovery(): void {
  if (globalThis.window == undefined) return
  globalThis.window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkOrAssetFailure(event.reason)) return
    event.preventDefault()
    tryReloadForChunkError()
  })
}

setupChunkLoadErrorRecovery()

// Vue core's <Transition> leave hook forces a reflow via ownerDocument.body.offsetHeight
// without a null check (forceReflow in @vue/runtime-dom, still present in 3.5.35); body can
// be null in detached/headless contexts. Not reachable from app code, so suppress (VIP-12034).
// info is 'transition hook' in dev builds and an error-reference URL ending in the
// numeric code (TRANSITION_HOOK = 9) in production builds.
function isTransitionReflowFailure(error: unknown, info: string): boolean {
  const isTransitionHook =
    info === 'transition hook' || String(info).endsWith('runtime-9')
  return (
    isTransitionHook &&
    error instanceof TypeError &&
    String(error.message).includes('offsetHeight')
  )
}

// An async IIFE, not top-level await: the browser build target (es2020 /
// safari14) does not support TLA, and esbuild fails the client build on it.
void (async () => {
  await newRelicReadyPromise

  // A serialized Pinia payload marks a server-rendered page: hydrate into the
  // existing markup. Without one this is a plain SPA route: cold-mount. The
  // same bundle serves both (VIP-12037).
  const ssrState = window.__PINIA_STATE__
  // The payload goes in through the factory: stores created during plugin
  // install (cms) must already see it, or their hydration is dropped.
  const { app, vuetify, router } = await createVicampoApp({
    hydrate: Boolean(ssrState),
    initialState: ssrState,
    ssrViewport: window.__SSR_VIEWPORT__
  })

  // Vue app-wide error handler: reports to backend (→ New Relic, source: vue_error_handler), keeps console.error for devtools
  app.config.errorHandler = (error, instance, info) => {
    if (isChunkOrAssetFailure(error as Error, undefined, info)) {
      // Suppress even when the once-per-session reload is already spent (VIP-12034)
      tryReloadForChunkError()
      console.error('Vue Error (chunk/asset, not reported):', error)
      return
    }

    if (isTransitionReflowFailure(error, info)) {
      console.error('Vue Error (transition reflow, not reported):', error)
      return
    }

    import('@/services/ApiController')
      .then(({ default: ApiController }) => {
        ApiController.logFrontendError(
          error as Error,
          'vue_error_handler',
          'notice'
        )
      })
      .catch(() => {})
    console.error('Vue Error:', error)
  }

  // On a server-rendered page the route component is a lazy chunk; mounting
  // before the router resolved it makes RouterView's first render a comment
  // node — Vue then discards the entire server-rendered page as a hydration
  // mismatch and re-renders it client-side (a visible flash). isReady()
  // resolves once the initial navigation, including the route chunk, is done
  // (VIP-12038).
  if (ssrState) await router.isReady()
  app.mount(MOUNT_POINT)

  // getComponentDefaults ignores the theme: main.js passed the theme name
  // anyway, which strict TS rejects — the behavior is unchanged without it.
  if (localStorage.getItem(HIGH_CONTRAST_TOGGLE) === 'true') {
    vuetify.defaults.value = getComponentDefaults()
  }

  watch(
    () => vuetify.theme.global.name.value,
    () => {
      vuetify.defaults.value = getComponentDefaults()
    }
  )
})()
